import { StanBadge, StanButton } from '@/components';
import { createIconElement } from '@/lib/utils/createIconElement';
import { formatTimeTo12Hour, parseLocalDate } from '@/lib/utils/timeUtils';
import IconChevronLeft from '@/assets/icons/ic-chevron-left.svg';
import IconChevronRight from '@/assets/icons/ic-chevron-right.svg';
import type { Appointment } from '../types/appointment.types';

const ChevronLeftIcon = createIconElement(IconChevronLeft);
const ChevronRightIcon = createIconElement(IconChevronRight);

interface AppointmentsListMobileProps {
    appointments: Appointment[];
    isLoading?: boolean;
    onAppointmentClick?: (appointment: Appointment) => void;
    page?: number;
    totalPages?: number;
    onPageChange?: (page: number) => void;
}

export function AppointmentsListMobile({
    appointments,
    isLoading = false,
    onAppointmentClick,
    page = 1,
    totalPages,
    onPageChange,
}: AppointmentsListMobileProps) {
    if (isLoading) {
        return (
            <div className='space-y-3'>
                {[...Array(3)].map((_, i) => (
                    <div key={i} className='bg-neutral-subtle h-24 animate-pulse rounded-lg' />
                ))}
            </div>
        );
    }

    if (appointments.length === 0) {
        return (
            <div className='flex flex-col items-center justify-center py-12 text-center'>
                <h3 className='text-dark mb-2 text-lg font-semibold'>No Appointments</h3>
                <p className='text-gray text-sm'>You don&apos;t have any appointments scheduled yet.</p>
            </div>
        );
    }

    return (
        <div className='space-y-3'>
            <div className='grid grid-cols-2 gap-4 pb-2'>
                <div className='text-dark text-body-sm font-semibold'>Date | Slot</div>
                <div className='text-dark text-body-sm font-semibold'>Title | Attendees</div>
            </div>

            <div className='space-y-3'>
                {appointments.map((appointment, index) => {
                    const date = parseLocalDate(appointment.date);
                    const isCanceled = appointment.status === 'canceled';

                    return (
                        <div
                            key={appointment.id || index}
                            onClick={() => onAppointmentClick?.(appointment)}
                            className='bg-canvas border-neutral-subtle grid cursor-pointer grid-cols-2 gap-4 rounded-lg border p-4 no-underline transition-shadow hover:shadow-md'
                        >
                            <div className='flex flex-col gap-1'>
                                <span className='text-dark text-body-sm font-medium no-underline'>
                                    {date.toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                    })}
                                </span>
                                <span className='text-gray text-body-xs no-underline'>
                                    {formatTimeTo12Hour(appointment.startTime)} -{' '}
                                    {formatTimeTo12Hour(appointment.endTime)}
                                </span>
                            </div>

                            <div className='flex flex-col gap-1'>
                                <span className='text-dark text-body-sm font-medium no-underline'>
                                    {appointment.title.length > 25
                                        ? `${appointment.title.substring(0, 25)}...`
                                        : appointment.title}
                                </span>
                                <span className='text-gray text-body-xs no-underline'>
                                    {typeof appointment.attendees === 'number'
                                        ? appointment.attendees === 1 &&
                                          appointment.attendeesList?.length === 1 &&
                                          appointment.attendeesList[0]
                                            ? appointment.attendeesList[0].email
                                            : `${appointment.attendees} Attendees`
                                        : appointment.attendees}
                                </span>
                                {isCanceled && (
                                    <StanBadge variant='destructive' size='sm' className='w-fit'>
                                        Canceled
                                    </StanBadge>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {totalPages && totalPages > 1 && (
                <div className='flex items-center justify-between pt-4'>
                    <span className='text-gray text-body-sm'>
                        Page {page} of {totalPages}
                    </span>
                    <div className='flex gap-2'>
                        <StanButton
                            name='previous-page'
                            onClick={() => onPageChange && page > 1 && onPageChange(page - 1)}
                            disabled={page <= 1}
                            icon={ChevronLeftIcon}
                            color='secondary'
                            variant='outline'
                            size='sm'
                            className='!size-8 !min-w-0 !p-0'
                        />
                        <StanButton
                            name='next-page'
                            onClick={() => onPageChange && page < totalPages && onPageChange(page + 1)}
                            disabled={page >= totalPages}
                            icon={ChevronRightIcon}
                            color='secondary'
                            variant='outline'
                            size='sm'
                            className='!size-8 !min-w-0 !p-0'
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
